import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, HStack, Text } from '@chakra-ui/react';
import { useStream } from '@laravel/stream-react';
import PropTypes from 'prop-types';

import { createToast } from '../../../../../redux/modules/app';
import { hideModal } from '../../../../../redux/modules/modal';
import colors from '../../../../../styles/theme/foundation/colors';

const AiExtractContractRequirementsModal = ({ companyId, contractIds, onComplete = templateId => {} }) => {
  const dispatch = useDispatch();
  const containerRef = useRef(null);

  const {
    data: requirementDataStream,
    isFetching: requirementDataStreamIsFetching,
    isStreaming: requirementStreamIsStreaming,
    send,
    cancel,
    error
  } = useStream(
    `/api/v1/companies/${companyId}/gpt-requests/contracts`,
    {
      headers: {
        Accept: 'text/event-stream',
        'Content-Type': 'application/json'
      },
      withCredentials: true
    }
  );

  // ================================
  // Buffered Streaming State
  // ================================

  const [displayedStream, setDisplayedStream] = useState('');
  const streamBufferRef = useRef('');
  const flushTimerRef = useRef(null);

  useEffect(() => {
    if (!requirementDataStream) return;

    // Update buffer only
    streamBufferRef.current = requirementDataStream;

    if (flushTimerRef.current) return;

    flushTimerRef.current = setTimeout(() => {
      setDisplayedStream(streamBufferRef.current);
      flushTimerRef.current = null;
    }, 40); // 30–50ms ideal
  }, [requirementDataStream]);

  // ================================
  // Smart Sticky Scroll
  // ================================

  const [isAtBottom, setIsAtBottom] = useState(true);

  const isUserNearBottom = (element, threshold = 80) => {
    if (!element) return false;

    const distance = element.scrollHeight - element.scrollTop - element.clientHeight;

    return distance < threshold;
  };

  const handleScroll = () => {
    const element = containerRef.current;
    if (!element) return;
    setIsAtBottom(isUserNearBottom(element));
  };

  const scrollToBottom = () => {
    const element = containerRef.current;
    if (!element) return;

    element.scrollTo({
      top: element.scrollHeight,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    if (!isAtBottom) return;

    element.scrollTop = element.scrollHeight;
  }, [displayedStream]);

  // ================================
  // Trigger AI Scan
  // ================================

  const triggerScan = () => {
    if (contractIds.length) {
      try {
        send({ contract_ids: contractIds });
      } catch (e) {
        dispatch(createToast({
          title: 'An error occurred while extracting requirements.',
          status: 'warning',
          duration: 5000
        }));
      }
    }
  };

  useEffect(() => {
    triggerScan();
  }, [contractIds]);

  // ================================
  // Connection timeout check
  // ================================

  const connectionTimeoutRef = useRef(null);
  const [showRetry, setShowRetry] = useState(false);

  const retryLogic = () => {
    triggerScan();
    setShowRetry(false);
  };

  useEffect(() => {
    // If we just started fetching, start timeout
    if (requirementDataStreamIsFetching) {
      connectionTimeoutRef.current = setTimeout(() => {
        // If still fetching and NOT streaming → connection likely failed
        if (requirementDataStreamIsFetching && !requirementStreamIsStreaming) {
          cancel(); // stop the request

          dispatch(createToast({
            title: 'Connection timed out.',
            description: 'Please click the "Retry" button. If you continue to experience connection issues, please contact support.',
            status: 'warning',
            duration: 7000
          }));

          setShowRetry(true);
        }
      }, 20000);
    }

    // If streaming starts or finishes, clear timeout
    if (requirementStreamIsStreaming || requirementDataStream) {
      clearTimeout(connectionTimeoutRef.current);
    }

    return () => {
      clearTimeout(connectionTimeoutRef.current);
    };
  }, [requirementDataStreamIsFetching, requirementStreamIsStreaming, requirementDataStream]);

  // ================================
  // Begin Data Parse After Stream Completes
  // ================================
  useEffect(() => {
    // if policy data exists and we are done streaming/fetching, then parse the data
    if (requirementDataStream && !requirementStreamIsStreaming && !requirementDataStreamIsFetching) {
      try {
        const splitOutput = requirementDataStream.split('New template id:');
        const templateId = splitOutput[1].trim();

        onComplete(templateId);
      } catch (e) {
        dispatch(createToast({
          title: 'PINS was unable to extract your contract requirements',
          description: 'Please click Retry to try again.',
          status: 'warning',
          duration: 7000
        }));

        setShowRetry(true);
      }
    }
  }, [requirementDataStream, requirementStreamIsStreaming, requirementDataStreamIsFetching]);

  return (
    <Box>
      <Box mb={2}>
        {requirementDataStreamIsFetching && <Text>Connecting...</Text>}
        {requirementStreamIsStreaming && <Text>Extracting Requirements...</Text>}
        {!requirementDataStreamIsFetching && !requirementStreamIsStreaming && requirementDataStream && <Text>Parsing Data...</Text>}
      </Box>

      <Box m="auto">
        <Box
          // bg={colors.white}
          bg="#f5f5f5"
          borderRadius="md"
          h="475px"
          position="relative"
        >
          <Box
            h="100%"
            overflowY="auto"
            px={4}
            ref={containerRef}
            onScroll={handleScroll}
          >
            <Box
              as="pre"
              color={colors.greyScale['900']}
              fontFamily="mono"
              fontSize="sm"
              m={0}
              whiteSpace="pre-wrap"
            >
              {displayedStream}
            </Box>

            {!isAtBottom && (
              <Button
                borderRadius="full"
                bottom="20px"
                boxShadow="lg"
                position="absolute"
                right="20px"
                size="sm"
                onClick={scrollToBottom}
              >
                ↓ New Content
              </Button>
            )}
          </Box>
        </Box>
        <Box mt={2}>
          <Text textAlign="center" textStyle="bodySm">PINS AI Assistant can make mistakes.</Text>
          <Text textAlign="center" textStyle="bodySm">Please verify that the requirement data is correct.</Text>
          <Text textAlign="center" textStyle="bodySm">PINS is not responsible or liable if required coverage is missing from output.</Text>

          <HStack justifyContent="center" mt={8}>
            {showRetry && <Button size="lg" onClick={() => retryLogic()}>Retry</Button>}
            <Button size="lg" variant="secondary" onClick={() => dispatch(hideModal())}>Cancel</Button>
          </HStack>
        </Box>
      </Box>
    </Box>
  );
};

AiExtractContractRequirementsModal.propTypes = {
  companyId: PropTypes.number.isRequired,
  contractIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  onComplete: PropTypes.func
};

export default AiExtractContractRequirementsModal;
